'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button, cn, Sheet, SheetContent, SheetTitle, SheetTrigger } from '@shared/components/shadcn';
import { ChevronLeft } from '@shared/icons';
import { useIsMobile } from '@shared/utils';
import type * as React from 'react';

type MyAccountSheetProps = {
  children: React.ReactNode;
  /**
   * NOTE: Trigger **MUST ALWAYS** be provided as an interative element (i.e. button) for accessibility reasons.
   * Controlling the sheet externally via `open` without using the trigger is an anti-pattern and breaks the accessible focus management provided by shadcn.
   */
  trigger: React.ReactNode;
  title?: string;
  description?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  footer?: React.ReactNode;
  desktopSide?: 'left' | 'right';
  mobileHeight?: string;
  desktopWidth?: string;
  onOpenAutoFocus?: (event: Event) => void;
  'data-track-feature'?: string;
  'data-track-container'?: string;
  'data-track-type'?: string;
  'data-testid'?: string;
};

function MyAccountSheet({
  children,
  trigger,
  title,
  description,
  open,
  onOpenChange,
  className,
  contentClassName,
  showBackButton = false,
  onBack,
  footer,
  desktopSide = 'right',
  mobileHeight = 'h-[90vh]',
  desktopWidth = 'max-w-md',
  onOpenAutoFocus,
  'data-track-feature': dataTrackFeature,
  'data-track-container': dataTrackContainer,
  'data-track-type': dataTrackType,
  'data-testid': dataTestId = 'my-account',
  ...props
}: MyAccountSheetProps) {
  const isMobile = useIsMobile();

  const getResponsiveClasses = () => {
    if (isMobile) {
      return cn('w-full max-w-none mx-0 p-0', mobileHeight, contentClassName);
    }
    return cn('w-full mx-auto p-0 h-[100vh] rounded-none ', desktopWidth, contentClassName);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} {...props}>
      {/* SheetTitle required for accessibility, but hidden as we don't need it */}
      <SheetTitle className="hidden" data-testid="sheet-title">
        {title}
      </SheetTitle>
      {/* Hidden description for accessibility */}
      <DialogPrimitive.Description className="sr-only" data-testid="sheet-description">
        {description || title}
      </DialogPrimitive.Description>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side={isMobile ? 'bottom' : desktopSide}
        className={getResponsiveClasses()}
        closeIconSize="extra-small"
        onOpenAutoFocus={onOpenAutoFocus}
        {...(dataTrackFeature && { 'data-track-feature': dataTrackFeature })}
        {...(dataTrackContainer && { 'data-track-container': dataTrackContainer })}
        {...(dataTrackType && { 'data-track-type': dataTrackType })}
      >
        <div className={cn('flex flex-col h-full', className)} data-testid="my-account-sheet-content">
          {/* Header */}
          {(title || showBackButton) && (
            <div className="flex items-center justify-between p-4 border-b border-border shrink-0 min-h-[60px]">
              {showBackButton ? (
                <div className="flex items-center">
                  <Button data-testid="back-button" aria-label="Go back" variant="secondary" className="rounded-full p-2 h-8 w-8" onClick={onBack} data-track-name="Back">
                    <ChevronLeft size="large" />
                  </Button>
                </div>
              ) : (
                <div className="h-8 w-8" /> // Spacer when no back button
              )}
              {title && (
                <div className="flex-1 flex items-center justify-center">
                  <SheetTitle className="paragraph-explanatory-strong" data-testid={`${dataTestId}-header-title`}>
                    {title}
                  </SheetTitle>
                </div>
              )}
              <div className="h-8 w-8" /> {/* Spacer for close button area */}
              {/* <SheetClose asChild>
                <Button variant="secondary" className="rounded-full p-2 h-8 w-8" onClick={onBack}>
                  <Close size="small" />
                </Button>
              </SheetClose> */}
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="px-4 py-2 border-b shrink-0">
              <p className="paragraph" data-testid="sheet-description-visible">
                {description}
              </p>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-col flex-1 overflow-y-auto">
            {children}
            {/* Footer */}
            {footer && <div className="p-4 mt-auto md:mt-0">{footer}</div>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Renders a hidden button with the provided label.
 * @deprecated A hidden trigger is an anti-pattern and should be avoided if at all possible. This component is provided for cases where the sheet needs to be controlled externally via the `open` prop without using a trigger, however, focus will be lost when the sheet is closed.
 * @param props.label The label for the sheet trigger.
 */
function HiddenTrigger({ label }: { label: string }) {
  return <button hidden>{label}</button>;
}

export { HiddenTrigger, MyAccountSheet };
